import { Dispatch, PropsWithChildren, SetStateAction, useCallback, useState } from 'react';
import {
  CampaignType_Enum as CampaignType,
  List,
  Recipient,
  Segment,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { FilterOptions } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import { pickBy } from 'lodash-es';
import type { UpsertAudience } from '@frontend/api-bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { BulkEmailPrefixes, BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { ContentLoader, contextFactory, useAlert } from '@frontend/design-system';
import { Mode, View } from '../types';

type AudienceTrayContextState = {
  // Mode
  expectModeToBe: (expectedModes: Mode[], callerName: string) => void;
  mode: Mode;
  setMode: (mode: Mode) => void;
  view: View;

  // Recipients
  initialRecipients: Recipient[];
  selectedRecipients: Recipient[];
  setSelectedRecipients: Dispatch<SetStateAction<Recipient[]>>;
  stashRecipients: () => void;
  restoreRecipients: () => void;

  // Lists
  initialListIds: string[];
  selectedListIds: string[];
  setSelectedListIds: Dispatch<SetStateAction<string[]>>;
  listsSearchQuery: string;
  setListsSearchQuery: (query: string) => void;
  listToUpdate: List | undefined;
  setListToUpdate: (list: List | undefined) => void;

  // Filters
  filters: FilterOptions;
  setFilters: Dispatch<SetStateAction<FilterOptions>>;
  stashFilters: () => void;
  restoreFilters: () => void;

  // Other
  campaignId: string;
  campaignType: CampaignType;
  closeTray: () => void;
  deleteSegments: (segments: Segment[]) => void; // TODO: Handle errors and loading
  enableManualSelection: boolean;
  isUpsertingAudience: boolean;
  locationIds: string[];
  startLoading: (message?: string, onCancel?: () => void) => void;
  stopLoading: () => void;
  trackingIdPrefix: string;
  upsertAudience: () => Promise<void>;
};

export const [AudienceTrayContext, useAudienceTray] = contextFactory<AudienceTrayContextState>(
  'useAudienceTray must be used within an AudienceTrayProvider'
);

type AudienceTrayProviderProps = PropsWithChildren<{
  campaignId: string;
  campaignType: CampaignType;
  closeTray: () => void;
  deleteSegments: (segments: Segment[]) => void;
  enableManualSelection: boolean;
  initialListIds: string[];
  initialRecipients: Recipient[];
  isUpsertingAudience: boolean;
  locationIds: string[];
  upsertAudience: UpsertAudience;
}>;

export const AudienceTrayProvider = ({
  campaignId,
  campaignType,
  children,
  closeTray,
  deleteSegments,
  enableManualSelection,
  initialListIds,
  initialRecipients,
  isUpsertingAudience,
  locationIds,
  upsertAudience: executeUpsert,
}: AudienceTrayProviderProps) => {
  const { t } = useTranslation('bulk-messaging');
  const { error: alertError } = useAlert();

  // Mode
  const [mode, setMode] = useState<Mode>(getDefaultView(locationIds.length));
  const view = mode.split('-')[0] as View;
  const expectModeToBe = useCallback(
    (expectedModes: Mode[], callerName: string) => {
      if (!expectedModes.includes(mode)) {
        throw new Error(
          `Mode '${mode}' is not valid in ${callerName}(). Valid modes are '${expectedModes.join("', '")}'.`
        );
      }
    },
    [mode]
  );

  // Recipients
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>(initialRecipients);
  const [stashedRecipients, setStashedRecipients] = useState<Recipient[]>([]);

  const stashRecipients = useCallback(() => {
    setStashedRecipients(selectedRecipients);
    setSelectedRecipients([]);
  }, [selectedRecipients]);

  const restoreRecipients = useCallback(() => {
    setSelectedRecipients(stashedRecipients);
    setStashedRecipients([]);
  }, [stashedRecipients]);

  // Lists
  const [selectedListIds, setSelectedListIds] = useState<string[]>(initialListIds);
  const [listsSearchQuery, setListsSearchQuery] = useState('');
  const [listToUpdate, setListToUpdate] = useState<List>();

  // Filters
  const [filters, setFiltersState] = useState<FilterOptions>({});
  const setFilters = useCallback((value: SetStateAction<FilterOptions>) => {
    setFiltersState((prevFilters) => {
      const newFilters = typeof value === 'function' ? value(prevFilters) : value;
      const sanitizedFilterOptions = pickBy(
        newFilters,
        (newFilterValue) =>
          newFilterValue !== null &&
          newFilterValue !== undefined &&
          newFilterValue !== '' &&
          !(Array.isArray(newFilterValue) && newFilterValue.length === 0)
      );
      return sanitizedFilterOptions;
    });
  }, []);
  const [stashedFilters, setStashedFilters] = useState<FilterOptions>({});

  const stashFilters = useCallback(() => {
    setStashedFilters(filters);
    setFilters({});
  }, [filters, setFilters]);

  const restoreFilters = useCallback(() => {
    setFilters(stashedFilters);
    setStashedFilters({});
  }, [stashedFilters, setFilters]);

  // Other
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentLoadingMessage, setContentLoadingMessage] = useState<string | undefined>(undefined);

  const startLoading = useCallback((message?: string) => {
    setIsContentLoading(true);
    setContentLoadingMessage(message);
  }, []);

  const stopLoading = useCallback(() => {
    setIsContentLoading(false);
    setContentLoadingMessage(undefined);
  }, []);

  const trackingIdPrefix =
    campaignType === CampaignType.EMAIL ? BulkEmailPrefixes.Audience : BulkTextPrefixes.Recipients;

  const upsertAudience = useCallback(async () => {
    expectModeToBe(['recipients', 'filters', 'lists'], 'upsertAudience');
    try {
      if (view === 'recipients') await executeUpsert({ recipients: selectedRecipients });
      if (view === 'filters') await executeUpsert({ filters });
      if (view === 'lists') await executeUpsert({ listIds: selectedListIds });
      closeTray();
    } catch (error) {
      alertError(t('Failed to update audience. Please try again.'));
      console.error('Failed to upsert audience:', error);
      sentry.error({
        team: 'fe-messaging',
        topic: 'messages',
        error: 'Error upserting audience',
        severityLevel: 'error',
        addContext: {
          name: 'Network error',
          context: {
            error,
          },
        },
      });
    }
  }, [expectModeToBe, view, executeUpsert, selectedRecipients, filters, selectedListIds, closeTray, alertError, t]);

  return (
    <AudienceTrayContext.Provider
      value={{
        // Mode
        expectModeToBe,
        mode,
        setMode,
        view,

        // Recipients
        initialRecipients,
        selectedRecipients,
        setSelectedRecipients,
        stashRecipients,
        restoreRecipients,

        // Lists
        initialListIds,
        selectedListIds,
        setSelectedListIds,
        listsSearchQuery,
        setListsSearchQuery,
        listToUpdate,
        setListToUpdate,

        // Filters
        filters,
        setFilters,
        stashFilters,
        restoreFilters,

        // Other
        campaignId,
        campaignType,
        closeTray,
        deleteSegments,
        enableManualSelection,
        isUpsertingAudience,
        locationIds,
        startLoading,
        stopLoading,
        trackingIdPrefix,
        upsertAudience,
      }}
    >
      <ContentLoader show={isContentLoading} message={contentLoadingMessage} />
      {children}
    </AudienceTrayContext.Provider>
  );
};

const getDefaultView = (locationsCount: number): View => {
  if (locationsCount === 0) return 'empty';
  if (locationsCount === 1) return 'recipients';
  return 'segments';
};
